import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Collapse, IconButton, Grid } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const ReadOnlyQuestion = ({ question, index }) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  // 1. Format the "Type" string to match the screenshot (lowercase usually, or sentence case)
  // Screenshot shows: "Type: multiple choice | Options: 5 | Select: min 1, max 1"
  const getQuestionTypeLabel = (type) => {
    switch (type) {
      case 'open-ended': return 'open ended';
      case 'multiple-choice': return 'multiple choice';
      case 'open-list': return 'open list';
      default: return type;
    }
  };

  const typeLabel = getQuestionTypeLabel(question.questionType);
  const isMultipleChoice = question.questionType === 'multiple-choice' || question.questionType === 'open-list';

  // 2. Build the metadata string found in the screenshot
  const getMetaString = () => {
    let parts = [`Type: ${typeLabel}`];

    if (isMultipleChoice) {
      const optCount = question.responseOptions ? question.responseOptions.length : 0;
      // Only show Options count for multiple choice/open list if appropriate
      if (question.questionType === 'multiple-choice') {
         parts.push(`Options: ${optCount}`);
      }
      
      // Select logic
      if (question.selectMin || question.selectMax) {
        let subParts = [];
        if (question.selectMin) subParts.push(`min ${question.selectMin}`);
        if (question.selectMax) subParts.push(`max ${question.selectMax}`);
        parts.push(`Select: ${subParts.join(', ')}`);
      }
    } else if (question.questionType === 'open-ended') {
      parts.push(`Max length: ${question.maxLength || 200}`);
    }

    return parts.join(' | ');
  };

  const metaString = getMetaString();

  // Helper component to mimic the "Input" box from the edit screen (Screenshot 2)
  const ReadOnlyInputDisplay = ({ label, value }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, ml: 0.5 }}>
        {label}
      </Typography>
      <Box
        sx={{
          width: '100%',
          p: 1.5,
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.23)',
          borderRadius: 1,
          color: 'text.primary',
          bgcolor: '#fafafa', // Slight grey to indicate read-only/disabled
          fontSize: '1rem',
        }}
      >
        {value}
      </Box>
    </Box>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: 1,
        // Mimic the clean white look
        borderColor: 'rgba(0,0,0,0.12)',
        boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.05)', 
      }}
    >
      {/* Clicking the content area toggles expand, 
        making it feel like "Click to view details" analogous to "Click to edit" 
      */}
      <CardContent 
        sx={{ 
          pb: '16px !important', 
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.01)' } 
        }}
        onClick={handleExpandClick}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          
          {/* Main Question Info */}
          <Box sx={{ flexGrow: 1, pr: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600, // Matching the bold look in Screenshot 1
                fontSize: '1.1rem',
                color: 'text.primary',
                mb: 0.5
              }}
            >
              Q{index + 1}: {question.questionText || 'Question text'}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {metaString}
            </Typography>
          </Box>

          {/* Expand Icon (Replaces the Edit/Delete icons from the normal view) */}
          <IconButton
            size="small"
            sx={{ 
                mt: -0.5, 
                color: 'text.secondary',
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        {/* Expanded Content - Mimics Screenshot 2 (Edit View) but Read-Only */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px dashed rgba(0,0,0,0.1)' }}>
            
            {/* 1. Min/Max Row (if applicable) - Mimics the 'Select Min'/'Select Max' inputs */}
            {(question.selectMin || question.selectMax) && (
                <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs={6} md={3}>
                        <ReadOnlyInputDisplay label="Select Min" value={question.selectMin || '0'} />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <ReadOnlyInputDisplay label="Select Max" value={question.selectMax || '0'} />
                    </Grid>
                </Grid>
            )}

            {/* 2. Options List - Mimics the Option Inputs */}
            {isMultipleChoice && question.responseOptions && (
              <Box>
                {question.responseOptions.map((opt, i) => (
                  <ReadOnlyInputDisplay 
                    key={i} 
                    label={`Option ${i + 1}`} 
                    value={opt || '(Empty option)'} 
                  />
                ))}
              </Box>
            )}

            {/* 3. Open Ended Details */}
            {question.questionType === 'open-ended' && (
                 <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                        <ReadOnlyInputDisplay label="Max Length" value={question.maxLength || 200} />
                    </Grid>
                 </Grid>
            )}

          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default ReadOnlyQuestion;